import ConfirmDialog from '@components/Shared/ConfirmDialog';
import Header from '@components/Shared/Layout/Header';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Box, Button, Card, CardContent, Chip, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import { useGetLicensesQuery } from '@store/api/licenseEndpoints';
import {
  useGetSeatSessionsQuery, useRevokeSeatSessionMutation, type SeatSession
} from '@store/api/seatSessionEndpoints';
import { useState } from 'react';

const formatAgo = (iso: string) => {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;

  return `${Math.round(seconds / 3600)}h ago`;
};

const formatCountdown = (seconds: number) => {
  if (seconds <= 0) return 'now';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;

  return `${Math.round(seconds / 3600)}h`;
};

const SeatSessionsPage = () => {
  // Seats change as clients come and go; poll rather than show a frozen picture.
  const { data: sessions = [], refetch, isFetching } = useGetSeatSessionsQuery(undefined, {
    pollingInterval: 30_000
  });
  const { data: licenses = [] } = useGetLicensesQuery();
  const [revokeSeatSession] = useRevokeSeatSessionMutation();

  // The seat the administrator is about to take off a device, held until they confirm it.
  const [releasing, setReleasing] = useState<SeatSession>();

  const licenseById = new Map(licenses.map(license => [license.licenseId, license]));

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Seat sessions'
        description={'A client keeps its seat by calling back every 15 minutes. Once a seat has ' +
          'gone quiet past that window, past the allocation time and past any reservation, it is ' +
          'handed to the next client automatically.'}
      >
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
      </Header>

      {sessions.length === 0 ? (
        <Card><CardContent>
          <Typography variant='body2' color='text.secondary'>No seats are in use.</Typography>
        </CardContent></Card>
      ) : (
        <Card><CardContent>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>License</TableCell>
                <TableCell>App</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Last heartbeat</TableCell>
                <TableCell>Reclaimable in</TableCell>
                <TableCell align='right' />
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map(session => (
                <TableRow key={session.sessionId}>
                  <TableCell>
                    <code>{session.id}</code>
                    <Typography variant='caption' display='block' color='text.secondary'>
                      {licenseById.get(session.licenseId)?.label ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size='small' variant='outlined' label={session.app ?? session.packageIdentifier} />
                  </TableCell>
                  <TableCell>
                    {session.deviceName || session.deviceId.slice(0, 8)}
                    <Typography variant='caption' display='block' color='text.secondary'>
                      {session.deviceModel}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {session.operatingSystem || session.runtimePlatform || '-'}
                    <Typography variant='caption' display='block' color='text.secondary'>
                      {session.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {/* Relative reads better at a glance; the exact stamp is one hover away. */}
                    <Tooltip title={new Date(session.lastUpdate).toFormattedDateTimeString()}>
                      <span>{formatAgo(session.lastUpdate)}</span>
                    </Tooltip>
                    {session.stale && <Chip size='small' color='warning' label='stale' sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>
                    {formatCountdown(session.reclaimableInSeconds)}
                    {session.allocationTimeRequest &&
                      new Date(session.allocationTimeRequest) > new Date() &&
                      <Chip size='small' color='info' label='reserved' sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align='right'>
                    <Tooltip title='Release this seat'>
                      <IconButton
                        size='small'
                        aria-label='Release seat'
                        onClick={() => setReleasing(session)}
                      >
                        <LinkOffIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {releasing && (
        <ConfirmDialog
          title='Release this seat?'
          confirmLabel='Release seat'
          confirmColor='warning'
          onClose={() => setReleasing(undefined)}
          onConfirm={() => revokeSeatSession({
            licenseId: releasing.licenseId, sessionId: releasing.sessionId
          })}
        >
          The seat returns to the pool at once and the next client can take it.{' '}
          <strong>{releasing.deviceName || releasing.deviceId.slice(0, 8)}</strong> keeps running
          on what it already has, but its token is dead. At its next call, within fifteen minutes,
          it has to be granted a seat again, and if <code>{releasing.id}</code> is full by then it
          loses access until one frees up.
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default SeatSessionsPage;
